/**
 * Warwick Design System - Style Mixins
 * Reusable style patterns and utilities
 */

import { css } from 'styled-components';
import { colors, typography, spacing, transforms, borderRadius } from './index';
import { tokens } from './tokens';

// Layout Mixins
export const flexCenter = css`
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const flexCenterVertical = css`
  display: flex;
  align-items: center;
`;

export const flexColumn = css`
  display: flex;
  flex-direction: column;
`;

export const flexSpaceBetween = css`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

// Card Mixins
export const cardBase = css`
  background-color: ${colors.surface.card};
  border-radius: ${borderRadius.card};
  border: 1px solid ${colors.border.primary};
`;

export const cardPadding = css`
  padding: ${spacing.card.padding};
`;

export const warwickCard = css`
  ${cardBase}
  ${cardPadding}
  ${flexColumn}
  color: ${colors.text.primary};

  i {
    color: ${colors.text.accent};
  }
`;

// Typography Mixins

// Shared base applied to every text mixin so Inter is always explicit,
// regardless of what the parent element inherits.
export const fontBase = css`
  font-family: ${tokens.primitive.typography.fontFamily.base};
`;

export const textWeatherTime = css`
  ${fontBase}
  font-size: ${tokens.primitive.typography.fontSize[37]};
  font-weight: ${tokens.primitive.typography.fontWeight.medium};
  line-height: ${tokens.primitive.typography.lineHeight.tight};
  color: ${tokens.semantic.color.text.onLight};
`;

export const textWelcomeHeading = css`
  ${fontBase}
  font-size: ${tokens.primitive.typography.fontSize[26]};
  font-weight: ${tokens.primitive.typography.fontWeight.extraBold};
  line-height: ${tokens.primitive.typography.lineHeight.tight};
  color: ${tokens.semantic.color.text.onLight};
`;

export const textGuestName = css`
  ${textWelcomeHeading}
  font-weight: ${tokens.primitive.typography.fontWeight.regular};
`;

export const textRoomNumber = css`
  ${fontBase}
  font-size: ${tokens.primitive.typography.fontSize[17]};
  font-weight: ${tokens.primitive.typography.fontWeight.regular};
  line-height: ${tokens.primitive.typography.lineHeight.tight};
  color: ${tokens.semantic.color.text.onLight};
`;

export const textRoomDetails = css`
  ${textRoomNumber}
  font-size: ${tokens.primitive.typography.fontSize[11]};
`;

export const textWifiHeading = css`
  font-size: ${typography.wifiHeading.fontSize};
  font-weight: ${typography.wifiHeading.fontWeight};
  line-height: ${typography.wifiHeading.lineHeight};
  color: ${colors.text.primary};
`;

export const textQrCode = css`
  font-size: ${typography.qrCodeText.fontSize};
  font-weight: ${typography.qrCodeText.fontWeight};
  line-height: ${typography.qrCodeText.lineHeight};
  color: ${colors.text.accent};
`;

export const textServiceTitle = (props) => css`
  ${fontBase}
  font-size: ${props.active
    ? tokens.primitive.typography.fontSize[39]
    : tokens.primitive.typography.fontSize[30]};
  font-weight: ${tokens.primitive.typography.fontWeight.semiBold};
  line-height: ${tokens.primitive.typography.lineHeight.tight};
  color: ${colors.text.onDark};
`;

export const textServiceDescription = (props) => css`
  ${fontBase}
  font-size: ${props.active
    ? tokens.primitive.typography.fontSize[26]
    : tokens.primitive.typography.fontSize[19]};
  font-weight: ${tokens.primitive.typography.fontWeight.regular};
  line-height: ${tokens.primitive.typography.lineHeight.tight};
  color: ${colors.text.onDark};
`;

export const navButton = css`
  position: relative;
  width: ${tokens.semantic.size.nav.buttonSize};
  height: ${tokens.semantic.size.nav.buttonSize};
  border-radius: 50%;
  border: none;
  background: transparent;
  color: ${tokens.semantic.color.text.onDark};
  ${flexCenter}
  cursor: pointer;

  i {
    font-size: ${tokens.semantic.size.nav.iconSize};
  }
`;

// Service Card Mixins
export const serviceCardContainer = css`
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: ${tokens.primitive.radius[39]};
  overflow: hidden;
  cursor: pointer;
`;

export const serviceCardActive = css`
  transform: ${transforms.serviceCard.active};
`;

export const serviceCardInactive = css`
  transform: ${transforms.serviceCard.inactive};
`;

export const serviceCardImage = css`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

export const serviceCardImageActive = css`
  opacity: 1;
  filter: none;
`;

export const serviceCardImageInactive = css`
  opacity: 0.5;
  filter: blur(4px);
`;

// Icon Mixins
export const iconAvatar = css`
  width: ${spacing['4xl']};
  height: ${spacing['4xl']};
  background-color: ${colors.interactive.avatar};
  border-radius: 50%;
  ${flexCenter}

  i {
    font-size: ${spacing['2xl']};
    color: ${colors.text.inverse};
  }
`;

export const iconWifi = css`
  width: 3.5rem;
  height: 2.25rem;
  color: ${colors.interactive.avatar};
`;

export const iconQrCode = css`
  width: ${spacing['8xl']};
  height: ${spacing['8xl']};
  color: ${colors.interactive.qrCode};
`;

export const surfaceSidebar = css`
  background-color: ${colors.surface.sidebar};
`;

export const surfaceNavBackground = css`
  background: ${tokens.semantic.color.background.nav};
  border-radius: ${tokens.primitive.radius[66]};
`;

// Notification Mixins
export const notificationDot = css`
  position: absolute;
  top: ${tokens.primitive.space[4]};
  right: ${tokens.primitive.space[4]};
  width: ${tokens.semantic.size.nav.badge};
  height: ${tokens.semantic.size.nav.badge};
  border-radius: 50%;
  background: ${tokens.semantic.color.text.notification};
`;

// Utility Mixins
export const visuallyHidden = css`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

export const truncateText = css`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const resetButton = css`
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  cursor: pointer;
  font: inherit;
  color: inherit;
`;

export const accentColor = css`
  color: ${tokens.semantic.color.icon.accent};
`;
export const glass = (color = '#ffffff', shade = 0.12) => `
  background: linear-gradient(
    135deg,
    color-mix(in srgb, ${color} ${shade * 100}%, transparent),
    color-mix(in srgb, ${color} ${shade * 70}%, transparent)
  );



  box-shadow:
    0 8px 24px rgba(0, 0, 0, 0.18),
    0 2px 8px rgba(0, 0, 0, 0.08);
`;